import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { 
  ArrowRight,
  TrendingUp,
  Globe,
  Settings,
  Battery,
  Lightbulb,
  Cpu,
  ShieldCheck,
  Target,
  Eye,
  Heart,
  Award,
  Layers,
  ArrowUpRight
} from "lucide-react";
import "./About.css";

// About Images
import aboutspinoffmain from "../img/about/about-spinoff-main.jpg";
import spinofftechnoparkabout from "../img/about/spinoff-technopark-about.jpg";
import ledlights from "../img/about/led-lights.jpg";
import lithiumbattery from "../img/about/lithium-battery.jpg";
import upsinverter from "../img/about/ups-inverter.jpg";
import sheetmetal from "../img/about/sheet-metal.jpg";

import mohalivideo from "../video/mohaliintro.mp4";

const About = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    window.scrollTo(0, 0);
  }, []);

  const verticals = [
    { title: "LED Excellence", img: ledlights, link: "/our-services/led-lights" },
    { title: "Lithium Energy", img: lithiumbattery, link: "/our-services/lithium-battery" },
    { title: "Inverter & UPS", img: upsinverter, link: "/our-services/inverter-ups" },
    { title: "Sheet Metal Hub", img: sheetmetal, link: "/our-services/sheet-metal" }
  ];

  return (
    <div className={`about-container ${isVisible ? 'is-ready' : ''}`}>
      <Helmet>
        <title>About Us | Spinoff Technopark Ltd</title>
        <link rel="preload" href={aboutspinoffmain} as="image" />
      </Helmet>

      {/* 3D ELITE HERO */}
      <section className="about-hero">
        <div className="about-hero-content">
          <div className="hero-pill" style={{background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)', color: '#6366f1', display: 'inline-flex', padding: '12px 24px', borderRadius: '100px', fontSize: '11px', fontWeight: '900', letterSpacing: '0.2em', marginBottom: '32px', border: '1px solid rgba(255,255,255,0.05)'}}>
             <ShieldCheck size={14} style={{marginRight: '8px'}} />
             35+ YEARS OF INDUSTRIAL LEGACY
          </div>
          <h1 className="hero-h1">Engineering a <span className="highlight-gradient">Smarter Earth.</span></h1>
          <p>
            SPINOFF Technopark Ltd is a multi-sectoral titan, architecting high-performance 
            ecosystems in green energy, intelligent lighting, and precision hardware.
          </p>
          <div style={{marginTop: '60px', display: 'flex', gap: '20px', justifyContent: 'center'}}>
             <button className="btn-3d btn-3d-primary">Our Legacy <ArrowRight size={20} /></button>
             <button className="btn-3d btn-3d-outline" style={{borderColor: 'rgba(255,255,255,0.2)'}}>View Portfolio</button>
          </div>
        </div>
      </section>

      {/* CORE IDENTITY - THE BENTO GRID */}
      <section className="pillars-section">
        <div className="max-w">
          <div className="pillars-grid">
            
            <div className="pillar-card col-8 bg-gradient-lite">
               <span style={{color: '#6366f1', marginBottom: '24px', display: 'block'}}><Target size={48} /></span>
               <h3>Our Vision.</h3>
               <p style={{fontSize: '22px', fontWeight: '400', lineHeight: '1.5', color: '#18181b'}}>
                 To become a world leader in creating energy-efficient products that positively impact the global environment. 
                 By reinforcing our belief that our future is our customers' happiness, we create ecosystems that last for generations.
               </p>
               <div style={{marginTop: '40px', display: 'flex', gap: '16px'}}>
                  <div className="highlight-glass" style={{padding: '12px 24px', fontSize: '12px', fontWeight: '900', color: '#6366f1'}}>ENERGY EFFICIENCY</div>
                  <div className="highlight-glass" style={{padding: '12px 24px', fontSize: '12px', fontWeight: '900', color: '#a855f7'}}>GLOBAL REACH</div>
               </div>
            </div>

            <div className="pillar-card col-4" style={{background: '#fafafa'}}>
               <span style={{color: '#a855f7', marginBottom: '24px', display: 'block'}}><Eye size={48} /></span>
               <h3>The Mission.</h3>
               <p>Reinforcing our belief in customer-centric innovation through SMART LED lighting, SMPS, and lithium energy solutions.</p>
               <div style={{marginTop: 'auto', paddingTop: '40px'}}>
                  <Link to="/our-services/led-lights" style={{display: 'flex', alignItems: 'center', gap: '8px', color: '#6366f1', fontWeight: '900', fontSize: '13px'}}>EXPLORE VERTICALS <ArrowUpRight size={16} /></Link>
               </div>
            </div>

            <div className="pillar-card col-4" style={{borderLeft: '4px solid #6366f1'}}>
               <span style={{color: '#6366f1', marginBottom: '24px', display: 'block'}}><Heart size={48} /></span>
               <h3>Core Values.</h3>
               <p>Innovation, sustainability, and excellence drive our commitment to quality across every industrial vertical.</p>
            </div>

            <div className="pillar-card col-8" style={{background: '#18181b', color: '#fff', padding: 0, border: 'none'}}>
               <div style={{padding: '60px', position: 'relative', zIndex: 2}}>
                  <span className="year-badge" style={{color: '#6366f1'}}>THE HUB</span>
                  <h3 style={{color: '#fff'}}>Advanced Infrastructure.</h3>
                  <p style={{color: '#a1a1aa'}}>
                    North India’s premier ISO and BIS certified plant specializes in LED manufacturing, Lithium storage, and high-performance hardware.
                  </p>
                  <Link to="/our-infrastructure" className="site-button-secondry btn-half" style={{marginTop: '40px', display: 'inline-flex'}}><span>View Mega Factory</span></Link>
               </div>
               <div className="bento-orb" style={{opacity: 0.1, background: 'radial-gradient(circle, #6366f1 0%, transparent 70%)', top: '-20%', right: '-20%'}} />
            </div>

          </div>
        </div>
      </section>

      {/* ELITE STATS SECTION */}
      <section className="stats-section">
        <div className="max-w">
          <div className="stats-grid">
            <div className="stat-item">
               <h4>35+</h4>
               <p>Years Legacy</p>
            </div>
            <div className="stat-item">
               <h4>700+</h4>
               <p>Product SKUs</p>
            </div>
            <div className="stat-item">
               <h4>250K</h4>
               <p>Sq Ft Facility</p>
            </div>
            <div className="stat-item">
               <h4>10+</h4>
               <p>Awarded Years</p>
            </div>
          </div>
        </div>
      </section>

      {/* VERTICALS PREVIEW */}
      <section className="verticals-preview" style={{padding: '120px 0'}}>
        <div className="max-w">
          <div style={{marginBottom: '60px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end'}}>
             <div>
                <span className="year-badge">PORTFOLIO</span>
                <h3 style={{fontSize: '40px', fontWeight: '900'}}>Our Business <span className="highlight-gradient">Verticals.</span></h3>
             </div>
             <Link to="/our-services/led-lights" style={{color: '#6366f1', fontWeight: '800', marginBottom: '10px'}}>SEE ALL SERVICES <ArrowRight size={14} style={{display: 'inline', marginLeft: '4px'}} /></Link>
          </div>
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px'}}>
             {verticals.map((v, i) => (
               <Link to={v.link} key={i} className="vertical-card" style={{textDecoration: 'none'}}>
                  <div className="factory-img-wrap" style={{borderRadius: '40px', overflow: 'hidden', height: '350px'}}>
                    <img src={v.img} alt={v.title} style={{height: '100%', width: '100%', objectFit: 'cover'}} />
                    <div className="factory-badge" style={{bottom: '30px', left: '30px'}}>{v.title}</div>
                  </div>
               </Link>
             ))}
          </div>
        </div>
      </section>

      {/* VIDEO PREVIEW */}
      <section style={{padding: '100px 0', background: 'var(--bg-dark)', borderRadius: '100px', margin: '0 40px'}}>
        <div className="max-w">
           <div style={{display: 'flex', gap: '80px', alignItems: 'center'}}>
              <div style={{flex: 1}}>
                 <span className="year-badge">EXPERIENCE CENTER</span>
                 <h2 style={{color: '#fff', fontSize: '44px', fontWeight: '900', marginBottom: '32px'}}>Watch Our <span className="highlight-gradient">R&D Power.</span></h2>
                 <p style={{color: '#a1a1aa', fontSize: '18px', marginBottom: '40px'}}>
                    Experience the state-of-the-art SMT lines from Japan and our specialized R&D hub in Mohali. 
                    Building the future of electronic excellence.
                 </p>
                 <a href={mohalivideo} target="_blank" className="btn-3d btn-3d-primary">Watch The Tour</a>
              </div>
              <div style={{flex: 1}}>
                 <div className="video-section-full" style={{backgroundImage: `url(${spinofftechnoparkabout})`, height: '400px', borderRadius: '40px', position: 'relative', overflow: 'hidden'}}>
                    <div className="overlay-main bg-black opacity-04" />
                    <div style={{position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                       <div className="ripple" style={{width: '80px', height: '80px', background: '#6366f1', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                          <Layers color="#fff" />
                       </div>
                    </div>
                 </div>
              </div>
           </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section style={{padding: '160px 0', textAlign: 'center'}}>
        <div className="max-w">
          <h2 style={{fontSize: '56px', fontWeight: '900', letterSpacing: '-2px'}}>Ready to engineer <span className="highlight-gradient">your success?</span></h2>
          <p style={{color: '#71717a', maxWidth: '600px', margin: '0 auto 60px'}}>Connect with our engineering experts to start your next high-performance industrial project.</p>
          <button className="btn-3d btn-3d-primary" style={{margin: '0 auto'}}>Connect with Experts</button>
        </div>
      </section>

    </div>
  );
};

export default About;
